import React, { useState, useEffect, useCallback } from "react";
import {
  Backdrop,
  Box,
  Fade,
  Modal,
  withStyles,
  createGenerateClassName,
  createTheme,
  StylesProvider,
  ThemeProvider,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PhoneIcon from "@material-ui/icons/Phone";
import SmsIcon from "@material-ui/icons/Sms";
import EmailIcon from "@material-ui/icons/Email";
import EmojiPeopleIcon from "@material-ui/icons/EmojiPeople";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import leadsApi from "../../../apis/leadsApi";

const generateClassName = createGenerateClassName({
  productionPrefix: "c",
  disableGlobal: true,
  seed: "lead-history-modal",
});

const theme = createTheme({
  typography: {
    fontFamily: "'Nunito', 'Varela Round', sans-serif",
  },
});

const ModalBox = withStyles((muiTheme) => ({
  root: {
    backgroundColor: "#ffffff",
    padding: 28,
    borderRadius: 16,
    position: "relative",
    maxWidth: 640,
    width: 640,
    maxHeight: "86vh",
    display: "flex",
    flexDirection: "column",
    "&:focus-visible": {
      outline: "none",
    },
    [muiTheme.breakpoints.down("sm")]: {
      width: "94vw",
      padding: 18,
    },
  },
}))(Box);

// The four ways a human touches a lead. System entries (campaigns,
// automations) arrive from the server already tagged.
const CHANNELS = [
  { key: "call", label: "Call", Icon: PhoneIcon },
  { key: "text", label: "Text", Icon: SmsIcon },
  { key: "email", label: "Email", Icon: EmailIcon },
  { key: "in_person", label: "In person", Icon: EmojiPeopleIcon },
];

const channelMeta = (channel) =>
  CHANNELS.find((c) => c.key === channel) || {
    key: channel,
    label: "System",
    Icon: FlashOnIcon,
  };

const KIND_TAGS = {
  campaign_email: "Campaign email",
  automation_sms: "Automated text",
};

const formatWhen = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  const stamp = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
  if (days <= 0) return `Today · ${stamp}`;
  if (days === 1) return `Yesterday · ${stamp}`;
  return `${days} days ago · ${stamp}`;
};

const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-CA"); // YYYY-MM-DD on this computer's calendar
};

// Contact history for one lead: every touch, human or machine, newest
// first — plus the log-a-touch form whose second half schedules the NEXT
// touch. That pairing is the whole point: no lead leaves this modal
// without a next step unless you explicitly clear it.
const LeadHistoryModal = ({ isOpen, onClose, lead, onLeadUpdated }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("call");
  const [note, setNote] = useState("");
  const [nextOn, setNextOn] = useState(lead?.next_follow_up_on || plusDays(7));
  const [nextNote, setNextNote] = useState(lead?.next_follow_up_note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchHistory = useCallback(async () => {
    if (!lead?.id) return;
    try {
      setLoading(true);
      const response = await leadsApi.getHistory(lead.id);
      setHistory(response?.history || []);
    } catch (e) {
      console.error("Error fetching history:", e);
    } finally {
      setLoading(false);
    }
  }, [lead?.id]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleLog = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const response = await leadsApi.logTouchpoint(lead.id, {
        channel,
        note,
        nextFollowUpOn: nextOn || null,
        nextFollowUpNote: nextNote || null,
      });
      setNote("");
      fetchHistory();
      if (onLeadUpdated && response?.lead) onLeadUpdated(response.lead);
    } catch (e) {
      console.error("Error logging touchpoint:", e);
      setError("Couldn't save that, please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    try {
      await leadsApi.deleteTouchpoint(lead.id, entry.id);
      setHistory((prev) => prev.filter((e) => !(e.id === entry.id && e.kind === entry.kind)));
    } catch (e) {
      console.error("Error deleting touchpoint:", e);
    }
  };

  if (!lead) return null;

  return (
    <StylesProvider generateClassName={generateClassName}>
      <ThemeProvider theme={theme}>
        <Modal
          open={isOpen}
          onClose={onClose}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 300 }}
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Fade in={isOpen}>
            <ModalBox>
              <IconButton
                size="small"
                onClick={onClose}
                style={{ position: "absolute", top: 14, right: 14 }}
              >
                <CloseIcon />
              </IconButton>

              <Typography style={{ fontSize: 20, fontWeight: 600 }}>
                Contact history
              </Typography>
              <Typography style={{ fontSize: 14, color: "#4F4F4F", marginBottom: 16 }}>
                {lead.first_name} {lead.last_name}
              </Typography>

              {/* Log a touch */}
              <Box
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 12,
                  padding: 14,
                  marginBottom: 18,
                }}
              >
                <Box style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {CHANNELS.map(({ key, label, Icon }) => (
                    <Button
                      key={key}
                      size="small"
                      variant={channel === key ? "contained" : "outlined"}
                      color={channel === key ? "primary" : "default"}
                      startIcon={<Icon style={{ fontSize: 16 }} />}
                      onClick={() => setChannel(key)}
                      style={{ borderRadius: 20, textTransform: "none" }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
                <TextField
                  fullWidth
                  placeholder="What happened? (left voicemail, spoke with mom, chatted after class…)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  multiline
                  minRows={1}
                  variant="outlined"
                  size="small"
                />
                <Box
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    marginTop: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    type="date"
                    label="Next follow-up"
                    value={nextOn || ""}
                    onChange={(e) => setNextOn(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    style={{ width: 170 }}
                  />
                  <TextField
                    placeholder="Why? (try after work hours…)"
                    value={nextNote}
                    onChange={(e) => setNextNote(e.target.value)}
                    size="small"
                    style={{ flex: 1, minWidth: 160 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLog}
                    disabled={saving}
                    style={{ borderRadius: 20, textTransform: "none", minWidth: 110 }}
                  >
                    {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Log touch"}
                  </Button>
                </Box>
                {error && (
                  <Typography style={{ color: "#B91C1C", fontSize: 13, marginTop: 8 }}>
                    {error}
                  </Typography>
                )}
              </Box>

              {/* Timeline */}
              <Box style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
                {loading ? (
                  <Box style={{ display: "flex", justifyContent: "center", padding: 30 }}>
                    <CircularProgress size={26} />
                  </Box>
                ) : history.length === 0 ? (
                  <Typography
                    style={{ color: "#6B7280", fontSize: 14, textAlign: "center", padding: 24 }}
                  >
                    No touches logged yet, the first call starts the relationship.
                  </Typography>
                ) : (
                  history.map((entry) => {
                    const { Icon } = channelMeta(entry.channel);
                    return (
                      <Box
                        key={`${entry.kind}-${entry.id}`}
                        style={{
                          display: "flex",
                          gap: 12,
                          padding: "10px 4px",
                          borderBottom: "1px solid #F3F4F6",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: entry.deletable ? "#EEF2FF" : "#F9FAFB",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          <Icon style={{ fontSize: 17, color: entry.deletable ? "#4C51BF" : "#6B7280" }} />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Box style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <Typography style={{ fontSize: 13, fontWeight: 600 }}>
                              {channelMeta(entry.channel).label}
                            </Typography>
                            {KIND_TAGS[entry.kind] && (
                              <Chip
                                label={KIND_TAGS[entry.kind]}
                                size="small"
                                style={{ height: 18, fontSize: 10, backgroundColor: "#F3F4F6" }}
                              />
                            )}
                            <Typography style={{ fontSize: 12, color: "#6B7280" }}>
                              {formatWhen(entry.occurred_at)}
                              {entry.logged_by ? ` · ${entry.logged_by}` : ""}
                            </Typography>
                          </Box>
                          {entry.note && (
                            <Typography style={{ fontSize: 13, color: "#374151", marginTop: 2, whiteSpace: "pre-wrap" }}>
                              {entry.note}
                            </Typography>
                          )}
                        </Box>
                        {entry.deletable && (
                          <Tooltip title="Remove entry">
                            <IconButton size="small" onClick={() => handleDelete(entry)}>
                              <DeleteOutlineIcon style={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </ModalBox>
          </Fade>
        </Modal>
      </ThemeProvider>
    </StylesProvider>
  );
};

export default LeadHistoryModal;
